/**
 * Transport plan — which relays the pool uses and the serverless last resort.
 *
 *  1. Direct WebRTC peer (accepted contacts, while both apps are open)
 *  2. Operated ("private") relays — bootstrap ∪ custom − disabled   (green)
 *  3. Community relays — curated public pool, on by default         (amber)
 *     Sender and recipient meet via rendezvous hashing on the recipient key.
 *  4. Supabase realtime + table — only when no relay acknowledged     (red)
 */

import {
  SupabaseRealtimeTransport,
  composeEffectiveRelays,
  resolveCommunityRelays,
  resolveSupabaseConfig,
  supabaseChannelForHint,
  type PoolRelay,
  type SinceStore,
} from "@veto/transport";
import type { Contact } from "@veto/domain";
import { loadNetPrefs, type NetPrefs } from "../netPrefs";
import { messagingDeviceIdHex } from "./sessionDevices";

let supabaseTransport: SupabaseRealtimeTransport | null = null;

/** Shared realtime fallback transport; null when Supabase env is unconfigured. */
export function getSupabaseTransport(): SupabaseRealtimeTransport | null {
  if (!supabaseTransport) {
    const cfg = resolveSupabaseConfig();
    if (!cfg) return null;
    supabaseTransport = new SupabaseRealtimeTransport(cfg);
  }
  return supabaseTransport;
}

/** 16-hex rendezvous / legacy routing hint of a messaging key. */
export function routingHint(messagingPublicKeyHex: string): string {
  return messagingDeviceIdHex(messagingPublicKeyHex).slice(0, 16);
}

export function recipientHint(contact: Contact): string {
  return routingHint(contact.messagingPublicKeyHex);
}

export function selfRealtimeTopic(selfMessagingPublicKeyHex: string): string {
  return supabaseChannelForHint(routingHint(selfMessagingPublicKeyHex));
}

export function recipientRealtimeTopic(contact: Contact): string {
  return supabaseChannelForHint(recipientHint(contact));
}

/** Relays the pool should keep (private + community unless the user disabled public relays). */
export function poolRelays(prefs: NetPrefs = loadNetPrefs()): PoolRelay[] {
  const out: PoolRelay[] = composeEffectiveRelays().map((r) => ({
    url: r.url,
    tier: r.tier === "community" ? "community" : "private",
  }));
  if (prefs.publicMesh) {
    for (const r of resolveCommunityRelays()) out.push({ url: r.url, tier: "community" });
  }
  return out;
}

const SINCE_KEY = "veto.relay.since";

/** Per-relay catch-up watermarks (created_at seconds) in localStorage — no content. */
export const localSinceStore: SinceStore = {
  load: () => {
    try {
      const raw = localStorage.getItem(SINCE_KEY);
      const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(parsed)) if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
      return out;
    } catch {
      return {};
    }
  },
  save: (map) => {
    try {
      localStorage.setItem(SINCE_KEY, JSON.stringify(map));
    } catch {
      /* quota / private mode — catch-up falls back to the full retention window */
    }
  },
};
